import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { displayErrorDetails } from '@brands/Utils/displayError';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import moment from 'moment';
import React, { useMemo } from 'react';
import { HiOutlinePencil } from 'react-icons/hi';
import { BsTrash3 } from 'react-icons/bs';

import { useWindowSize } from '../../../../../../../hooks';
import { useAppSelector } from '../../../../../../../hooks/useReduxHook';
import { checkUserAvailability } from '../../../../../../../services/availability/checkUserAvailability';
import { deleteUserAvailability } from '../../../../../../../services/availability/deleteUserAvailability';
import { ProviderAvailability } from '../../../../../../../services/availability/types/ProviderAvailability';
import { ICase, Status } from '../../../../../../../services/cases/types/ICase';
import { getPrimaryColor } from '../../../../../../../Utils/getPrimaryColor';
import styles from './styles.module.scss';

type GroupedDateOverrides = {
  date: string;
  dateOverrides: ProviderAvailability[];
}[];

type DateOverridesProps = {
  setAddDateModal: React.Dispatch<React.SetStateAction<boolean>>;
  dateOverrides: ProviderAvailability[];
  setDateOverrides: React.Dispatch<React.SetStateAction<ProviderAvailability[]>>;
  setAlertModal: React.Dispatch<
    React.SetStateAction<{
      cases: ICase[];
      kind: 'override' | 'weekly';
      type: 'delete' | 'update' | 'add';
      selectedDates?: Date[];
      isUnavailable?: boolean;
      slots: ProviderAvailability[];
    } | null>
  >;
  requestSending: boolean;
  onEditOverride?: (dates: Date[]) => void;
};

const DateOverrides = ({
  setAddDateModal,
  dateOverrides,
  setDateOverrides,
  setAlertModal,
  requestSending,
  onEditOverride,
}: DateOverridesProps): JSX.Element => {
  const { userInfo } = useAppSelector(selectAuth);
  const screenSize = useWindowSize();
  const [loading, setLoading] = React.useState(false);

  const groupOverridesByDate = (overrides: ProviderAvailability[]): GroupedDateOverrides => {
    const groupedOverrides: GroupedDateOverrides = [];

    overrides.forEach((override) => {
      const { date } = override;
      if (moment(date).isBefore(moment().format('YYYY-MM-DD'))) {
        return;
      }
      const dateOverride = groupedOverrides.find((group) => group.date === date);

      if (dateOverride) {
        dateOverride.dateOverrides.push(override);
      } else {
        groupedOverrides.push({ date, dateOverrides: [override] });
      }
    });

    groupedOverrides.sort((a, b) => {
      const aDate = moment(a.date);
      const bDate = moment(b.date);
      if (aDate.isBefore(bDate)) {
        return -1;
      }
      if (aDate.isAfter(bDate)) {
        return 1;
      }
      return 0;
    });

    groupedOverrides.forEach((group) => {
      group.dateOverrides.sort((a, b) => {
        const aStartTime = moment(a.start_time, 'h:mmA').format('HH:mm:ss');
        const bStartTime = moment(b.start_time, 'h:mmA').format('HH:mm:ss');
        if (aStartTime < bStartTime) {
          return -1;
        }
        if (aStartTime > bStartTime) {
          return 1;
        }
        return 0;
      });
    });

    return groupedOverrides;
  };

  const groupedOverrides = useMemo(() => groupOverridesByDate(dateOverrides), [dateOverrides]);

  const deleteDateOverride = async (override: ProviderAvailability): Promise<void> => {
    await deleteUserAvailability(userInfo.id, override.id?.toString() || '')
      .then(() => {
        setDateOverrides((prevOverrides) => prevOverrides.filter((prevOverride) => prevOverride.id !== override.id));
      })
      .catch((error: unknown) => {
        displayErrorDetails(error);
      });
  };

  const handleDeleteDateOverride = (overrides: ProviderAvailability[]): void => {
    (async () => {
      setLoading(true);
      let res: ICase[] = [];
      if (UserRoleName.Provider === userInfo.role.name && (overrides[0].asynchronous || overrides[0].synchronous)) {
        try {
          res = await checkUserAvailability(userInfo.id, {
            availability_id: overrides[0].id || 0,
          }, userInfo.role.name);
        } catch (error: unknown) {
          displayErrorDetails(error);
          setLoading(false);
          return;
        }
      }
      if (
        res.length > 0 &&
        !res.every(
          (item) =>
            item.status === Status.Canceled || item.status === Status.Deleted || item.status === Status.Completed,
        )
      ) {
        setAlertModal({
          cases: res,
          kind: 'override',
          type: 'delete',
          slots: [overrides[0]],
        });
      } else {
        await deleteDateOverride(overrides[0]);
      }
      setLoading(false);
    })();
  };

  const handleEditOverride = (date: string): void => {
    if (onEditOverride) {
      onEditOverride([moment(date).toDate()]);
    }
  };

  return (
    <div className={styles.dateOverridesContainer}>
      <div className={styles.dateOverridesHeader}>Date overrides</div>
      <span className={styles.dateOverridesSpan}>
        Update when your availability differs from your regular daily schedule.
      </span>
      <div className={styles.dateOverridesBody}>
        <button
          className={`fs-unmask ${styles.addDateOverrideButton}`}
          type="button"
          onClick={() => {
            setAddDateModal(true);
          }}
        >
          <AddOutlinedIcon />
          Add an override
        </button>
        {groupedOverrides.length > 0 && (
          <div className={styles.overridesCard}>
            <div className={styles.header}>
              <span>Your overrides</span>
            </div>

            {!requestSending ? (
              groupedOverrides.map((date, dateIndex) => (
                <div key={date.date} className={styles.overrideRow}>
                  <div className={styles.date}>{moment(date.date).format('MMM DD, YYYY')}</div>

                  <div className={styles.times}>
                    {date.dateOverrides.length > 0 ? (
                      date.dateOverrides.map((override, index) => (
                        <div style={{ display: 'flex', flexDirection: 'row' }}>
                          <div key={override.id} className={styles.timeRow}>
                            <div className={styles.timeText}>
                              {override.start_time === '12:00am' && override.end_time === '11:59pm' ? (
                                <span className={styles.unavailable}>Unavail</span>
                              ) : (
                                <>
                                  <span>{override.start_time}</span>
                                  <span> – </span>
                                  <span>{override.end_time}</span>
                                </>
                              )}
                            </div>

                            {userInfo.role.name === UserRoleName.Provider && (
                              <div className={styles.types}>
                                {override.synchronous && <span>Sync</span>}
                                {override.asynchronous && override.synchronous && <span>, </span>}
                                {override.asynchronous && <span>Async</span>}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <span className={styles.unavailable}>Unavail</span>
                    )}
                  </div>
                  <div className={styles.dateOverrideActions}>
                    {onEditOverride && (
                      <button
                        type="button"
                        className={styles.editBtn}
                        onClick={() => {
                          handleEditOverride(date.date);
                        }}
                        disabled={loading}
                        aria-label="Edit Date Override"
                      >
                        <HiOutlinePencil size={18} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        handleDeleteDateOverride(date.dateOverrides);
                      }}
                      disabled={loading}
                      aria-label="Delete Date Override"
                    >
                      <BsTrash3 size={16} color="#D32F2F" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.loadingContainer}>
                <Loading dotted />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DateOverrides;
